import React, { useState, useRef, useEffect } from "react";
import { Button } from "react-bootstrap";
import axios from "axios";
import Notification, { showToast } from "../common/notification/Notification";
import { AiOutlineClockCircle } from "react-icons/ai";

const MobileVerification = ({
  mobileNumber,
  action,
  userType,
  onMobileVerified,
  disabled,
}) => {
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [isMobileVerified, setIsMobileVerified] = useState(false);
  const [timer, setTimer] = useState(0);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isValidatingOtp, setIsValidatingOtp] = useState(false);

  const baseUrl = process.env.REACT_APP_BASE_URL;
  const inputRefs = useRef([...Array(6)].map(() => React.createRef()));

  const sendOtp = async () => {
    if (!mobileNumber) {
      showToast("Please enter a valid mobile number.", { type: "error" });
      return;
    }
    setIsSendingOtp(true);
    try {
      const response = await axios.post(`${baseUrl}/all/otp/request`, {
        mobile: mobileNumber,
        action: action,
      });
      if (response.data.statusType === "SUCCESS") {
        setOtpSent(true);
        setTimer(2 * 60);
        showToast("OTP sent successfully to your mobile number!", {
          type: "success",
        });
      } else {
        setOtpSent(false);
        showToast(response.data.text || "Failed to send OTP.", {
          type: "error",
        });
      }
    } catch (error) {
      setOtpSent(false);
      showToast("An error occurred while sending OTP.", { type: "error" });
    } finally {
      setIsSendingOtp(false);
    }
  };

  const validateOtp = async () => {
    const otpValue = otp.join("");
    if (!mobileNumber || otpValue.length !== 6) {
      showToast("Please enter a valid 6-digit OTP.", { type: "error" });
      return;
    }
    setIsValidatingOtp(true);
    try {
      const response = await axios.post(`${baseUrl}/all/otp/verify`, {
        mobile: mobileNumber,
        otp: otpValue,
        user_type: userType,
      });
      if (response.data.statusType === "SUCCESS") {
        setIsMobileVerified(true);
        showToast("Mobile number verified successfully!", { type: "success" });
        onMobileVerified({
          statusType: response.data.statusType,
          accessToken: response.data.accessToken || response.data.token,
          token: response.data.token,
          refreshToken: response.data.refreshToken,
          user: response.data.user,
        });
      } else {
        showToast(response.data.text || "Invalid OTP.", { type: "error" });
      }
    } catch (error) {
      showToast("An error occurred during OTP validation.", { type: "error" });
    } finally {
      setIsValidatingOtp(false);
    }
  };

  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  const handleOtpChange = (e, idx) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    if (!value) return;
    const newOtp = [...otp];
    newOtp[idx] = value[0];
    setOtp(newOtp);
    if (idx < 5 && value) {
      inputRefs.current[idx + 1].current.focus();
    }
  };

  const handleKeyDown = (e, idx) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const newOtp = [...otp];
      if (otp[idx]) {
        newOtp[idx] = "";
        setOtp(newOtp);
      } else if (idx > 0) {
        inputRefs.current[idx - 1].current.focus();
        newOtp[idx - 1] = "";
        setOtp(newOtp);
      }
    }
  };

  const formatTimer = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${remainingSeconds
      .toString()
      .padStart(2, "0")}`;
  };

  if (isMobileVerified) return null;

  return (
    <div
      className="mobile-verification-container mx-auto"
      style={{ maxWidth: "400px" }}
    >
      <Notification />
      <div className="p-0">
        {!otpSent && (
          <Button
            type="button"
            className="w-100 mb-3 fw-semibold border-0"
            style={{ backgroundColor: "#1c7ed6" }}
            onClick={sendOtp}
            disabled={isSendingOtp || disabled}
          >
            {isSendingOtp ? "Sending..." : "Send OTP"}
          </Button>
        )}

        {otpSent && (
          <div className="otp-section mt-3">
            <label className="form-label fw-bold mb-2 text-center d-block">
              Enter 6-digit OTP
            </label>
            <small className="text-muted d-block mb-3 text-center">
              Sent to your mobile number
            </small>

            <div className="d-flex justify-content-center gap-2 mb-3">
              {otp.map((digit, idx) => (
                <input
                  key={idx}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  className="form-control text-center border-primary p-1"
                  style={{
                    maxWidth: "45px",
                    maxHeight: "50px",
                    fontSize: "1.5rem",
                    borderRadius: "8px",
                  }}
                  value={digit}
                  ref={inputRefs.current[idx]}
                  onChange={(e) => handleOtpChange(e, idx)}
                  onKeyDown={(e) => handleKeyDown(e, idx)}
                  disabled={isMobileVerified}
                />
              ))}
            </div>

            {timer > 0 && (
              <div className="d-flex justify-content-center mb-3">
                <div className="d-flex align-items-center gap-2">
                  <AiOutlineClockCircle size={18} className="text-danger" />
                  <span className="fw-semibold text-danger">
                    {formatTimer(timer)} left
                  </span>
                </div>
              </div>
            )}

            {timer === 0 && (
              <div className="text-center mt-2">
                <span className="text-muted">
                  Didn’t get the OTP?{" "}
                  <button
                    type="button"
                    className="btn btn-link p-0 fw-semibold text-decoration-none bg-transparent border-0"
                    onClick={sendOtp}
                    disabled={isSendingOtp}
                  >
                    {isSendingOtp ? "Resending..." : "Resend"}
                  </button>
                </span>
              </div>
            )}

            <Button
              type="button"
              className="btn btn-success w-100 fw-semibold mt-3"
              onClick={validateOtp}
              disabled={
                otp.join("").length !== 6 || isMobileVerified || isValidatingOtp
              }
            >
              {isValidatingOtp
                ? "Validating..."
                : isMobileVerified
                  ? "Verified"
                  : "Validate OTP"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileVerification;
